import { Component, Output, EventEmitter, ElementRef, Input, ViewChild, OnInit, AfterViewInit, SimpleChanges, OnChanges } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';

declare const google: any;

@Component({
  selector: 'app-address',
  standalone: true,
  imports: [
    FormsModule,
    MatInputModule,
    MatIconModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    CommonModule
  ],
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.css']
})
export class AddressComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() addressPlaceholder: string = 'Enter your address';
  @Input() zipPlaceholder: string = 'Zip Code';
  @Input() required: boolean = false;
  @Output() addressUpdated = new EventEmitter<{ billingaddress: string; billingzip: string }>();

  @ViewChild('searchInput') searchInput!: ElementRef;

  ngOnInit(): void {
    if (!this.formGroup) {
      console.error('formGroup is not defined in AddressComponent');
    } else {
      this.initializeFormControls();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formGroup'] && changes['formGroup'].currentValue) {
      this.initializeFormControls();
    }
  }

  onAddressChange(): void {
    if (this.formGroup) {
      this.addressUpdated.emit({
        billingaddress: this.formGroup.get('billingaddress')?.value || '',
        billingzip: this.formGroup.get('billingzip')?.value || '',
      });
    }
  }

  private initializeFormControls(): void {
    if (!this.formGroup) return;

    // Map "billingaddress" to "address" internally
    if (!this.formGroup.get('billingaddress')) {
      this.formGroup.addControl('billingaddress', new FormControl('', this.required ? Validators.required : null));
    }

    // Map "billingzip" to "postalCode" internally
    if (!this.formGroup.get('billingzip')) {
      this.formGroup.addControl('billingzip', new FormControl('', this.required ? Validators.required : null));
    }

    // Additional fields not included in the main form value
    if (!this.formGroup.contains('city')) {
      this.formGroup.registerControl('city', new FormControl(''));
    }
    if (!this.formGroup.contains('state')) {
      this.formGroup.registerControl('state', new FormControl(''));
    }
    if (!this.formGroup.contains('country')) {
      this.formGroup.registerControl('country', new FormControl(''));
    }
  }


  ngAfterViewInit() {
    this.initializeGoogleAutocomplete();
  }

  private initializeGoogleAutocomplete(): void {
    if (!this.searchInput) return;

    const autocomplete = new google.maps.places.Autocomplete(this.searchInput.nativeElement, {
      componentRestrictions: { country: 'US' },
      types: ['address'],
    });
    autocomplete.addListener('place_changed', () => this.handlePlaceChange(autocomplete));
  }

  private handlePlaceChange(autocomplete: any): void {
    const place = autocomplete.getPlace();
    if (!place.address_components) return;

    let streetNumber = '';
    let route = '';
    let locality = ''; // City
    let administrativeArea = ''; // State
    let country = '';
    let postalCode = '';

    // Extract components
    place.address_components.forEach((component: any) => {
      const types = component.types;
      if (types.includes('street_number')) streetNumber = component.long_name;
      if (types.includes('route')) route = component.long_name;
      if (types.includes('locality')) locality = component.long_name; // City
      if (types.includes('administrative_area_level_1')) administrativeArea = component.short_name; // State
      if (types.includes('country')) country = component.long_name;
      if (types.includes('postal_code')) postalCode = component.long_name;
    });

    // Construct full address
    const fullAddress = `${streetNumber} ${route}, ${locality}, ${administrativeArea}, ${country}`.trim();

    // Emit updated address
    this.addressUpdated.emit({
      billingaddress: fullAddress,
      billingzip: postalCode,
    });

    if (this.formGroup) {
      // Update form values
      this.formGroup.patchValue({
        billingaddress: fullAddress,
        billingzip: postalCode,
      });
    }
  }
  onInputAddress(event: any) {
    const value = event.target.value;
    event.target.value = value.replace(/[^a-zA-Z0-9\s]/g, '');
  }

  onInputZipCode(event: any) {
    const value = event.target.value;
    event.target.value = value.replace(/\D/g, '');
  }
}
